import { ClassName, Theme } from '@busybusy/webapp-react-ui';
import {
  MemberGpsStatusTypes,
  MemberPermissions,
  OperationType,
  QueryMemberGpsStatusesQuery,
  QueryMemberGpsStatusesQueryVariables,
} from '__generated__/graphql';
import { MEMBER_GPS_STATUS_QUERY } from 'apollo/queries/member-gps-status-queries';
import { PhotoIcon } from 'assets/icons';
import classNames from 'classnames';
import { dashboardCardBarChartGradientBlueLightPurple } from 'components/domain/dashboard/dashboard-card-chart/DashboardBarChart/constants/constants';
import DashboardCardBarChart from 'components/domain/dashboard/dashboard-card-chart/DashboardBarChart/DashboardBarChart';
import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import DashboardContentCard from 'components/foundation/dashboard/DashboardContentCard/DashboardContentCard';
import useDashboardGraphDates from 'containers/dashboard/hooks/useDashboardGraphDates';
import {
  assignItemsByCreationToRanges,
  convertDashboardTimeFrameToTimeRangeType,
  getDashboardTimeFrameTitle,
  zipDaysToValue,
} from 'containers/dashboard/util/utils';
import { getDashboardNavigationRange } from 'containers/scheduling/utils/utils';
import useGraphQLPaging from 'hooks/graphql/useGraphQLPaging/useGraphQLPaging';
import useGPSDisabledNavigation from 'hooks/navigation/useGPSDisabledNavigation';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import ITimeRange from 'types/TimeRange';
import { NonNullArrayField } from 'types/util/NonNullArrayField';
import { useFeatureFlags } from 'utils/features';
import DashboardProUpgradeCard from '../DashboardProUpgradeCard/DashboardProUpgradeCard';
import useDashboardCardTotal from '../hooks/useDashboardCardTotal';

export interface IGPSDisabledDashboardCardProps {
  className?: ClassName;
  theme: Theme.DARK | Theme.LIGHT;
  setting: IDashboardSettingsItem;
  onHide: () => void;
}

const GPSDisabledDashboardCard = (props: IGPSDisabledDashboardCardProps) => {
  const { className, theme, setting, onHide } = props;

  const isPro = useFeatureFlags('PRO');

  const timeFrame = setting.options?.time;
  const { timeRange, timeRanges } = useDashboardGraphDates(timeFrame !== 'daily' ? timeFrame : 'pastSeven')!;

  const getData = useGPSDisabledDashboardCardData(timeRange);
  const [disabledItems, setDisabledItems] = useState<GPSDisabledDashboardCardQueryItem[] | null>(null);
  const zippedValues = zipDaysToValue(assignItemsByCreationToRanges(timeRanges, disabledItems ?? []));

  const total = useDashboardCardTotal(
    timeRanges,
    null,
    null,
    zippedValues,
    setting.options?.time ?? 'payPeriod',
    false
  );

  const navigateToReport = useGPSDisabledNavigation();

  useEffect(() => {
    getData().then(setDisabledItems);
  }, [getData, setting]);

  function handleNavigation() {
    const navRange = getDashboardNavigationRange(timeRange, timeRanges, setting.options?.time ?? 'payPeriod', 'utc');

    navigateToReport({
      start_date: navRange.startTime.toISODate(),
      end_date: navRange.endTime.toISODate(),
      time_range_type: convertDashboardTimeFrameToTimeRangeType(setting.options?.time ?? 'payPeriod'),
    });
  }

  const classes = classNames('gps-disabled-dashboard-card', className);

  return (
    <>
      {isPro ? (
        <DashboardContentCard
          header={total}
          theme={theme}
          subheaderValue={getDashboardTimeFrameTitle(t('GPS Disabled'), setting.options?.time ?? 'monthly')}
          className={classes}
          rightLabel={t('Details')}
          onRightLabelClick={handleNavigation}
        >
          <DashboardCardBarChart
            data={zippedValues}
            dataKey="value"
            valueType="raw"
            timeFrame={timeFrame}
            theme={theme}
            {...dashboardCardBarChartGradientBlueLightPurple}
          />
        </DashboardContentCard>
      ) : (
        <DashboardProUpgradeCard title={setting.title} svg={PhotoIcon} theme={theme} onHide={onHide} />
      )}
    </>
  );
};

type GPSDisabledDashboardCardQueryItem = NonNullArrayField<QueryMemberGpsStatusesQuery['memberGpsStatuses']>;

export function useGPSDisabledDashboardCardData(timeRange: ITimeRange<DateTime>) {
  const getAll = useGraphQLPaging();

  return useCallback(async () => {
    return getAll<QueryMemberGpsStatusesQuery, QueryMemberGpsStatusesQueryVariables>(
      {
        document: MEMBER_GPS_STATUS_QUERY,
        variables: {
          filter: {
            deletedOn: { isNull: true },
            status: { equal: MemberGpsStatusTypes.Disabled },
            member: {
              permissions: {
                operationType: OperationType.And,
                permissions: [MemberPermissions.ManageGpsApproval],
                includeSelf: true,
              },
            },
            createdOn: {
              lessThanOrEqual: timeRange.endTime
                .minus(timeRange.endTime.toLocal().offset * 60 * 1000)
                .toISO({ suppressSeconds: true }),
              greaterThanOrEqual: timeRange.startTime
                .minus(timeRange.startTime.toLocal().offset * 60 * 1000)
                .toISO({ suppressSeconds: true }),
            },
          },
        },
      },
      'memberGpsStatuses'
    );
  }, [getAll, timeRange.startTime, timeRange.endTime]);
}

export default GPSDisabledDashboardCard;
